import React from 'react';
import { useController, ApplicationView } from '@aha-app/mvc';
import type AssistantViewController from '../AssistantView/AssistantViewController';
import type { CustomAgent } from '../AssistantView/AssistantViewController';
import styles from './PromptPanel.module.css';
import Agent from '../../agents/Agent';
import AhaAnalytics from 'javascripts/analytics';

const CustomAgentMenuItem = ({ agent, selectedAgentId, updateAgent }) => {
  return (
    <aha-menu-item
      selected={selectedAgentId === agent.id ? true : undefined}
      key={agent.id}
    >
      <a onClick={() => updateAgent(agent.id)}>{agent.name}</a>
    </aha-menu-item>
  );
};

const AgentSelection: React.FC<{
  setShowTemplateModalType: (type: string) => void;
}> = ({ setShowTemplateModalType }) => {
  const controller = useController<AssistantViewController>();
  const currentAgent = controller.agent;
  const { agentName, agentId } = controller.state;

  const updateAgent = (newAgent: string) => {
    controller.actionSetAgent(newAgent);
  };

  const builtInAgents = Agent.getAllAgents()
    .filter(agent => agent.name !== 'CustomUserAgent')
    .map(agent => {
      if (
        agent.available(
          controller.initialAgentParams,
          controller.initialAgentInstances
        )
      ) {
        return (
          <aha-menu-item
            key={agent.name}
            selected={agentName === agent.name ? true : undefined}
          >
            <a onClick={() => updateAgent(agent.name)}>{agent.publicName}</a>
          </aha-menu-item>
        );
      }
    })
    .filter(entry => entry);

  const mappedAgents: {
    favorites: CustomAgent[];
    personal: CustomAgent[];
    workspace: CustomAgent[];
  } = {
    favorites: [],
    personal: [],
    workspace: [],
  };
  controller.state.customAgents.map(agent => {
    if (agent.favorite) {
      mappedAgents.favorites.push(agent);
    }
    mappedAgents[agent.agentScope].push(agent);
  });

  const favoriteAgents = mappedAgents.favorites.map(agent => (
    <CustomAgentMenuItem
      key={agent.id}
      agent={agent}
      selectedAgentId={agentId}
      updateAgent={updateAgent}
    />
  ));

  if (builtInAgents.length < 2 && controller.state.customAgents.length === 0) {
    return null; // Only show the selection if there are multiple agents available
  }

  return (
    <div className={styles.agentSelection}>
      <aha-menu>
        <aha-button slot='control' size='small' kind='default'>
          <span style={{ color: 'var(--theme-tertiary-text)' }}>Agent</span>{' '}
          {currentAgent ? currentAgent.publicName() : 'Select Agent'}
          <i className='far fa-chevron-down' />
        </aha-button>
        <aha-menu-content>
          {builtInAgents.length > 0 && (
            <aha-menu-group key='built-in-agents'>
              {builtInAgents}
            </aha-menu-group>
          )}
          {favoriteAgents.length > 0 && (
            <aha-menu-group key='custom-agents'>
              <aha-menu-item>
                <aha-menu>
                  <div slot='control'>Favorites</div>
                  <aha-menu-content>{favoriteAgents}</aha-menu-content>
                </aha-menu>
              </aha-menu-item>
            </aha-menu-group>
          )}
          {(mappedAgents.personal.length > 0 ||
            mappedAgents.workspace.length > 0) && (
            <aha-menu-group key='custom-agents-group'>
              {mappedAgents.personal.length > 0 && (
                <aha-menu-item>
                  <aha-menu>
                    <div slot='control'>Personal</div>
                    <aha-menu-content>
                      {mappedAgents.personal.map(option => (
                        <CustomAgentMenuItem
                          key={option.id}
                          agent={option}
                          selectedAgentId={agentId}
                          updateAgent={updateAgent}
                        />
                      ))}
                    </aha-menu-content>
                  </aha-menu>
                </aha-menu-item>
              )}
              {mappedAgents.workspace.length > 0 && (
                <aha-menu-item>
                  <aha-menu>
                    <div slot='control'>Workspace</div>
                    <aha-menu-content>
                      {mappedAgents.workspace.map(option => (
                        <CustomAgentMenuItem
                          key={option.id}
                          agent={option}
                          selectedAgentId={agentId}
                          updateAgent={updateAgent}
                        />
                      ))}
                    </aha-menu-content>
                  </aha-menu>
                </aha-menu-item>
              )}
            </aha-menu-group>
          )}
          <aha-menu-group key='add-agent'>
            <aha-menu-item>
              <a
                href={`/settings/ai_agents/new?tab=agents&after_view=prompt_panel`}
                data-remote='true'
              >
                <i className='fa-regular fa-plus' /> New agent
              </a>
            </aha-menu-item>
            <aha-menu-item>
              <a
                onClick={() => {
                  setShowTemplateModalType('AGENT');
                  AhaAnalytics.track(
                    'Feature-7530436202602257500',
                    'User opens AI library',
                    {
                      'CTA used': 'AI assistant - Agent menu',
                    }
                  );
                }}
              >
                <i className='fa-regular fa-book' /> Search the library
              </a>
            </aha-menu-item>
          </aha-menu-group>
        </aha-menu-content>
      </aha-menu>
    </div>
  );
};

export default ApplicationView(AgentSelection);
